import { Connector } from '@/backendConnector';

const state = {
  projectLoading: false,
  isDataSaving: false,
  isCancelSaving: false,
  cancelStatus: null,
  step: '',
  token: '',
  projectId: '',
};

export const CREATED_STEP = 'CREATED';
export const LOADING_STEP = 'LOADING';
export const DELIVERY_STEP = 'DELIVERY';
export const SERVICE_STEP = 'SERVICES';
export const INVENTORY_STEP = 'INVENTORY';
export const COMPLETED_STEP = 'COMPLETED';

export const STEPS_TO_ROUTE = {
  [CREATED_STEP]: {
    index: 0,
    name: 'loadingAddress',
  },
  [LOADING_STEP]: {
    index: 1,
    name: 'deliveryAddress',
  },
  [DELIVERY_STEP]: {
    index: 2,
    name: 'serviceOfferDates',
  },
  [SERVICE_STEP]: {
    index: 3,
    name: 'furnituresRooms',
  },
  [INVENTORY_STEP]: {
    index: 4,
    name: 'validateConfirm',
  },
  [COMPLETED_STEP]: {
    index: 5,
    name: 'validateDone',
  },
};

function commitAddress(commit, store, object) {
  if (object.address) {
    commit(`${store}/SET_ADDRESS`, object.address, { root: true });
  }
  commit(`${store}/SET_LIFT`, object.floor.lift, { root: true });
  commit(`${store}/SET_ALL_IN_LIFT`, object.floor.all_in_lift, { root: true });
  commit(`${store}/SET_LIFT_COMMENT`, object.floor.lift_comment, { root: true });
  commit(`${store}/SET_STAIRS`, object.floor.stairs, { root: true });
  commit(`${store}/SET_MM_OBLIGATOIRE`, object.floor.mm_obligatoire, { root: true });
  commit(`${store}/SET_MM_UTILISABLE`, object.floor.mm_utilisable, { root: true });
  if (object.floor.floor !== undefined) {
    commit(`${store}/SET_FLOOR`, object.floor.floor, { root: true });
  }
  if (object.floor.cave !== undefined) {
    commit(`${store}/SET_CAVE`, object.floor.cave, { root: true });
  }
  if (object.floor.type_logement !== undefined) {
    commit(`${store}/SET_TYPES_LOGEMENT`, object.floor.type_logement, { root: true });
  }
  if (object.parking) {
    commit(`${store}/SET_PARKING_VL`, object.parking.vl, { root: true });
    commit(`${store}/SET_PARKING_PORTAGE`, object.parking.portage, { root: true });
    commit(`${store}/SET_PARKING_TYPE`, object.parking.type, { root: true });
  }
}

function floorAndParkingDefaultValue() {
  return {
    floor: {
      all_in_lift: null,
      floor: '',
      lift: null,
      lift_comment: '',
      mm_obligatoire: null,
      mm_utilisable: null,
      stairs: null,
      type_logement: null,
      cave: null,
    },
    parking: {
      type: '',
      portage: '',
      vl: null,
    },
  };
}
function loadingDefaultValue(project) {
  project.loading = {
    address: project.loading.address,
    ...floorAndParkingDefaultValue(),
  };
  return project;
}

function deliveryDefaultValue(project) {
  project.delivery = {
    address: project.delivery.address,
    ...floorAndParkingDefaultValue(),
  };
  return project;
}

function serviceDefaultValue(project) {
  project.services = {
    cartons: null,
    wardrobes: null,
    mount: null,
    fragile: null,
    unbreakable: null,
  };
  return project;
}

function insuranceDefaultValue(project) {
  project.insurance = {
    type: '',
    value: 0,
  };
}

function setProjectDefaultValue(project) {
  const { booking_step } = project;
  switch (booking_step) {
    case CREATED_STEP:
      loadingDefaultValue(project);
      deliveryDefaultValue(project);
      serviceDefaultValue(project);
      insuranceDefaultValue(project);
      break;
    case LOADING_STEP:
      deliveryDefaultValue(project);
      serviceDefaultValue(project);
      insuranceDefaultValue(project);
      break;
    case DELIVERY_STEP:
      serviceDefaultValue(project);
      insuranceDefaultValue(project);
      break;
    default:
      break;
  }

  if (project.delivery.address.street === '-'
    || project.delivery.address.street === `${project.delivery.address.city}, France`) {
    deliveryDefaultValue(project);
  }
  return project;
}

const actions = {
  setStep({ commit }, step) {
    const stepData = Connector.getInstance().setStep(step);
    commit('SET_STEP', stepData ? stepData.step : step);
  },
  getProject({ commit }) {
    commit('SET_PROJECT_LOADING', true);
    return Connector.getInstance()
      .getProjectRoomsAndCartons()
      .then(async ({ project, rooms, cartons }) => {
        commit('SET_PROJECT_ID', project.project);
        commit('SET_CANCEL_STATUS', project.is_canceled_by_client);
        commit('SET_STEP', project.booking_step);
        commit('SET_TOKEN', project.token);
        project = setProjectDefaultValue(project);
        commitAddress(commit, 'loadingStore', project.loading);
        commitAddress(commit, 'deliveryStore', project.delivery);
        commit('serviceOfferStore/SET_DATES', project.dates, { root: true });
        commit('serviceOfferStore/SET_SERVICES_OFFER', project.services, { root: true });
        commit('serviceOfferStore/SET_INSURANCE', project.insurance, { root: true });
        commit('furnituresStore/SET_ROOMS', rooms, { root: true });
        commit('furnituresStore/SET_CARTONS', cartons, { root: true });
      })
      .finally(() => {
        commit('SET_PROJECT_LOADING', false);
      });
  },
  cancelProject({ commit }, status) {
    commit('SET_CANCEL_SAVING', true);
    return Connector.getInstance()
      .cancelProject({ description: status.description || status.label, status: status.value })
      .then(() => {
        commit('SET_CANCEL_STATUS', status);
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => {
        commit('SET_CANCEL_SAVING', false);
      });
  },
  finishProject({ commit }) {
    commit('SET_DATA_SAVING', true);
    return Connector.getInstance().validateProject()
      .then(() => {
        commit('SET_CANCEL_STATUS', false);
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => {
        commit('SET_DATA_SAVING', false);
      });
  },
  resetAccessValues({ commit }, { store, data }) {
    commitAddress(commit, store, data);
  },
  emptyAction() {
    return Promise.resolve();
  },
};

const getters = {
  token: state => state.token,
  step: state => state.step,
  cancelStatus: state => state.cancelStatus,
  projectId: state => state.projectId,
  projectLoading: state => state.projectLoading,
  isDataSaving: state => state.isDataSaving,
  isCancelSaving: state => state.isCancelSaving,
};

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token;
  },
  SET_STEP(state, step) {
    state.step = step;
  },
  SET_PROJECT_ID(state, projectId) {
    state.projectId = projectId;
  },
  SET_PROJECT_LOADING(state, value) {
    state.projectLoading = value;
  },
  SET_DATA_SAVING(state, value) {
    state.isDataSaving = value;
  },
  SET_CANCEL_SAVING(state, value) {
    state.isCancelSaving = value;
  },
  SET_CANCEL_STATUS(state, status) {
    state.cancelStatus = status;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
